import { isMobile } from "@common_lib/base/utils/src/tools"
import loadSetting from "@/utils/loadSetting"
import { safeLocalStorage } from "@/utils/safeStorage"

type ResourceManifest = Record<string, string>

let resourceManifest: ResourceManifest | null = null

// Load resource manifest from JSON
const loadResourceManifest = async (): Promise<ResourceManifest> => {
  if (resourceManifest !== null) {
    return resourceManifest
  }

  try {
    const response = await fetch("/image-manifest.json")
    if (!response.ok) {
      throw new Error(`Failed to load resource manifest: ${response.statusText}`)
    }
    const manifest = (await response.json()) as ResourceManifest
    resourceManifest = manifest
    return manifest
  } catch (error) {
    console.error("Error loading resource manifest:", error)
    return {}
  }
}

// @ts-ignore - Vite env variables
const isDevelopment = import.meta.env.VITE_DOMAIN_CHANGE === "true"
// @ts-ignore - Vite env variables
const isDevMode = import.meta.env.MODE === "dev"

const getFileName = (websiteName: string) => {
  const uiCSSFileName = websiteName.toLowerCase()
  const forceCSSFileName = isDevelopment && safeLocalStorage.getItem("website")
  const fileName = forceCSSFileName || uiCSSFileName

  return fileName
}

const getForceTheme = () => {
  const forceCSSThemeName = isDevelopment ? safeLocalStorage.getItem("theme") : null

  return forceCSSThemeName
}

const appendChildInHead = (content: string, id: string) => {
  const style = document.createElement("style")
  style.id = id
  style.textContent = content

  document.head.appendChild(style)
}

const loadCustomSettingWithScss = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fallbackDeviceType = deviceType === "mobile" ? "desktop" : "mobile"
    const fileName = getFileName(websiteName)

    const manifest = await loadResourceManifest()

    // Find the CSS file path in manifest (compiled from SCSS in production)
    // In dev mode, use .scss (compiled by Vite plugin)
    // In production, use .css (pre-compiled during build)
    const fileExtension = isDevMode ? "scss" : "css"
    const targetPattern = `labels/${fileName}/${deviceType}/setting.${fileExtension}`
    const fallbackPattern = `labels/${fileName}/${fallbackDeviceType}/setting.${fileExtension}`

    const targetPath = Object.entries(manifest).find(([key]) =>
      key.includes(targetPattern)
    )
    const fallbackPath = Object.entries(manifest).find(([key]) =>
      key.includes(fallbackPattern)
    )

    const [, url] = targetPath || fallbackPath || []

    if (!url) throw new Error(`Invalid setting:${targetPattern}`)

    // Fetch and load the CSS content
    const response = await fetch(url)
    if (!response.ok) {
      console.error("Failed to fetch setting CSS:", {
        url,
        status: response.status,
        statusText: response.statusText
      })
      throw new Error(
        `Failed to load setting CSS: ${response.status} ${response.statusText}`
      )
    }

    const cssContent = await response.text()
    appendChildInHead(cssContent, "custom-setting")
  } catch (error) {
    console.error("Failed to import custom setting:", error)
  }
}

const applyCssStyle = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fileName = getFileName(websiteName)

    const setting = await loadSetting()
    const webSiteSetting = setting[fileName]
    if (!webSiteSetting) throw new Error("Invalid setting")

    const forceDSName = getForceTheme()
    const themes = forceDSName || webSiteSetting?.themes[deviceType]

    if (themes === undefined) throw new Error("Invalid template")

    const manifest = await loadResourceManifest()

    // Find the theme CSS file in manifest
    const themePattern = `themes/${deviceType}/${themes}.css`
    const themePath = Object.entries(manifest).find(([key]) => key.includes(themePattern))

    const [, url] = themePath || []

    if (!url) throw new Error("Invalid setting themes")

    // Fetch and load the CSS content
    const response = await fetch(url)
    if (!response.ok) throw new Error("Failed to load theme CSS")

    const cssContent = await response.text()
    appendChildInHead(cssContent, "custom-theme")
  } catch (error) {
    console.error("Failed to import custom style css:", error)
  }
}

const setFaviconHref = async (websiteName: string) => {
  try {
    const deviceType = isMobile() ? "mobile" : "desktop"
    const fallbackDeviceType = deviceType === "mobile" ? "desktop" : "mobile"
    const fileName = getFileName(websiteName)

    const manifest = await loadResourceManifest()

    // Find the favicon file in manifest
    const targetPattern = `labels/${fileName}/${deviceType}/images/favicon.ico`
    const fallbackPattern = `labels/${fileName}/${fallbackDeviceType}/images/favicon.ico`

    const targetPath = Object.entries(manifest).find(([key]) =>
      key.includes(targetPattern)
    )
    const fallbackPath = Object.entries(manifest).find(([key]) =>
      key.includes(fallbackPattern)
    )

    const [, url] = targetPath || fallbackPath || []

    // 取得或建立 favicon link 元素
    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement

    if (!url) {
      // 當找不到 favicon 時，移除 link 元素以避免瀏覽器載入預設 favicon
      // 例如：UAT 的 API 白牌都是使用同一個網址，如果不移除會導致 favicon 抓到前一次開啟的白牌的 favicon
      if (link) link.remove()
      throw new Error("Invalid faviconURL")
    }

    if (!link) {
      link = document.createElement("link")
      link.rel = "icon"
      document.head.appendChild(link)
    }

    link.href = url
  } catch (e) {
    console.error("Failed to create favicon URL:", e)
  }
}

const loadCustomSetting = async (websiteName: string) => {
  await applyCssStyle(websiteName)
  await loadCustomSettingWithScss(websiteName)
  await setFaviconHref(websiteName)
}

export default loadCustomSetting
